import { InputDateModal } from "core/inputs/input-date/InputDateModal";
import InputSelect from "core/inputs/input-select/InputSelect";
import InputText from "core/inputs/input-text/InputText";
import { FormattedMessage, useIntl } from "react-intl";
import { Table } from "core/table/Table";
import { Typography } from "core/typography/Typography";
import { getFormattedDate } from "utils/functions";
import PropTypes from "prop-types";
import {
  MAX_LENGTH_SALARY,
  MODAL_CONTENT,
  POPUP_STATUS,
  QUERY_STATUS,
  VAR_TYPES,
} from "constants/constants";
import { useState, useEffect } from "react";
import {
  onChange,
  onDelete,
  renderCommonBlock,
} from "utils/tableContentHelper";
import _ from "lodash";
import { phoneSize } from "utils/screenSizes";
import AlertPopup from "components/alert-popup/AlertPopup";
import { useUserDatatrackContext } from "api/datatrack/UserDatatrackProvider";
import ModalFooter from "components/modal/ModalFooter";
import { updatedByDataHelper } from "utils/modalsHelper";
import { format } from "date-fns";
import SalaryValidationModal from "components/modal/BalancesheetSalaryModal";
import {
  ERROR_AT_LEAST_ONE_SALARY,
  ERROR_MISSING_SALARY_OR_ESTABLISHMENT_DATE,
  ERROR_SALARY_ESTABLISHMENT_DATE,
} from "constants/popup-codes";
import { getSalaryCoefPosWarning } from "api/datatrack/salary/queries";

const ManageSalary = ({
  currencyListByBranch,
  currencyDefault,
  userSalary,
  getUpdatedValues,
  lang,
  setElementsToDelete,
  onClose,
  onValidate,
  entityStatus,
  userScope,
  person_id,
  coefOptions,
  userCoefPositionList,
  syntecCoef,
  csltArrivalDate,
}) => {
  const currencyDefaultValue = currencyDefault[0]?.id || 1;
  const { apiErrorResponse } = useUserDatatrackContext();
  const { userDatatrackUpdateById } = useUserDatatrackContext();
  const [status, setStatus] = useState();
  const [code, setCode] = useState();
  const [showSalaryValidationModal, setShowSalaryValidationModal] =
    useState(false);
  const [salaryWarning, setSalaryWarning] = useState(null);
  const [lastEditedSalary, setLastEditedSalary] = useState(null);
  const [lastEditedSalaryRowId, setLastEditedSalaryRowId] = useState(null);
  // const intl = useIntl();

  useEffect(() => {
    if (apiErrorResponse?.["establishment_date"]) {
      setCode("api_error_establishment_date");
      setStatus(POPUP_STATUS.ERROR);
    }
    if (apiErrorResponse?.value) {
      setCode("api_error_salary");
      setStatus(POPUP_STATUS.ERROR);
    }
  }, [apiErrorResponse]);

  const checkSalaryData = () => {
    for (const salary of data) {
      const missingValueError = !salary.establishment_date || !salary.value;
      /** TEMPORARY HOTFIX (HF-0040)
       *
       * When a consultant is created, a line is created in the ‘Manage Salaries’ modal with establishment date = start date.
       * If this start date is changed via the ‘Manage Contract’ modal to a later date,
       * the “establishmentDateError” error is triggered every time a line is created or updated in the ‘Manage Salaries’ modal.
       *
       * DO NOT REMOVE this until the backend has synchronised the start date in the ‘Manage Salaries’ modal when it is modified in
       * the ‘Manage Contract’ modal (estimated 20 July 2026)
       */

      // const establishmentDateError =
      //   salary.establishment_date < csltArrivalDate;

      if (missingValueError /* || establishmentDateError */) {
        setCode(
          // missingValueError
          //   ?
          ERROR_MISSING_SALARY_OR_ESTABLISHMENT_DATE
          // : `${intl.formatMessage({
          //     id: ERROR_SALARY_ESTABLISHMENT_DATE,
          //     defaultMessage:
          //       "La date de prise en compte ne peut être antérieure à la date d'arrivée du consultant",
          //   })} (${getFormattedDate(csltArrivalDate, lang)})`
        );
        setStatus(POPUP_STATUS.ERROR);
        return false;
      }
    }
    return true;
  };

  const formattedData = (data) =>
    data?.map((e) => ({
      comment: e.comment,
      currency_id: e.currency_id,
      establishment_date: e.establishment_date,
      value: e.value,
    }));

  const onClickValidate = async () => {
    const hasDataChanges =
      JSON.stringify(formattedData(initialData)) !==
      JSON.stringify(formattedData(data));
    // if data is invalid or nothing changed, stop here
    if (!checkSalaryData() || !hasDataChanges) {
      return;
    }
    // If no salary was edited, or if user wants to "impose" the salary, validate directly without opening the warning modal
    if (
      lastEditedSalary === null ||
      salaryWarning?.base_salary === lastEditedSalary
    ) {
      onValidate();
      return;
    }
    // Only validate the last edited salary
    const salaryToCheck = lastEditedSalary;

    try {
      //request to ask the backend if this salary is aligned with the current coef/pos
      const response = await getSalaryCoefPosWarning({
        scope: userScope,
        salary: salaryToCheck,
        person_id,
      });
      // If backend returns no warning, validate immediately
      const warning = response?.warning;

      if (!warning) {
        onValidate();
        return;
      }
      // Otherwise, show the salary warning modal
      setSalaryWarning(warning);
      setShowSalaryValidationModal(true);
    } catch (e) {
      console.error("getSalaryCoefPosWarning error", e);
    }
  };

  const handleSalaryValidationSubmit = async ({ salary, coef }) => {
    // close warning modal
    setShowSalaryValidationModal(false);

    const parsedSalary =
      salary !== "" && !isNaN(Number(salary)) ? Number(salary) : null;
    // Convert the selected coef/pos label into its corresponding ID
    const selectedCoef = syntecCoef?.find((c) => c.value === coef);
    const coefId = selectedCoef?.id;
    // 1) update salary in salary modal
    if (parsedSalary !== null && lastEditedSalaryRowId) {
      onChange({
        getUpdatedValues,
        id: lastEditedSalaryRowId,
        key: "value",
        setData,
        data,
        val: parsedSalary,
      });
      setLastEditedSalary(parsedSalary);
    }
    // 2) Update coef/pos if the user selected one in the modal
    if (coefId && userCoefPositionList?.length) {
      try {
        const currentCoefRow = userCoefPositionList[0];

        const payload = {
          id: currentCoefRow.id,
          person_id: currentCoefRow.person_id,
          start_date: currentCoefRow.start_date,
          backup_legacy: currentCoefRow.backup_legacy ?? null,
          syntec_coef_position: coefId,
          syntec_coef_position_staff: null,
        };

        await userDatatrackUpdateById({
          id: currentCoefRow.id,
          data: payload,
          model: MODAL_CONTENT.MANAGE_POSITION_COEF,
        });
      } catch (e) {
        console.error("Error update coef/pos from salary modal", e);
      }
    }
  };

  const onAddWithDefaultCurrency = () => {
    const newId = _.uniqueId();
    const keys = ["id", ...header.map((elm) => elm.label.key)];
    const newData = keys.reduce(
      (prev, curr) => ({
        ...prev,
        [curr]:
          curr === "id"
            ? newId
            : curr === "currency_id"
              ? currencyDefaultValue
              : null,
      }),
      { newRow: true }
    );
    setData([newData, ...data]);
  };

  const header = [
    {
      label: { key: "id", value: "id" },
      icon: "salary",
      width: "fit-content",
    },
    {
      label: {
        key: "value",
        value: (
          <FormattedMessage
            id="salary"
            defaultMessage="Salaire mensuel temps plein"
          />
        ),
      },
      icon: "salary",
      width: "fit-content",
    },
    {
      label: {
        key: "currency_id",
        value: <FormattedMessage id="currency" defaultMessage="Devise" />,
      },
      icon: "salary",
      width: "fit-content",
    },
    {
      label: {
        key: "comment",
        value: <FormattedMessage id="comment" defaultMessage="Commentaire" />,
      },
      icon: "clients",
      width: "fit-content",
    },
    {
      label: {
        key: "establishment_date",
        value: (
          <FormattedMessage
            id="establishment_date"
            defaultMessage="Date de prise en compte"
          />
        ),
      },
      icon: "user",
      width: "fit-content",
    },
    {
      label: {
        key: "updated_by",
        value: (
          <FormattedMessage id="updated_by" defaultMessage="Modifié par" />
        ),
      },
      icon: "user",
      width: "fit-content",
    },
    {
      label: {
        key: "updated_at",
        value: <FormattedMessage id="updated_at" defaultMessage="le" />,
      },
      icon: "calendar",
      width: "fit-content",
    },
  ];
  const [data, setData] = useState(userSalary);
  const [initialData, setInitialData] = useState(userSalary);

  useEffect(() => {
    if (initialData?.length < userSalary?.length && userSalary?.length > 0) {
      setData(userSalary);
      setInitialData(userSalary);
    }
  }, [initialData, userSalary]);

  useEffect(() => {
    if (userSalary?.length && data?.length) {
      updatedByDataHelper({ data, setData, modalData: userSalary });
    }
  }, [userSalary]);

  const formatCurrencyList = () =>
    currencyListByBranch?.map((elm) => ({
      value: elm.id,
      label: elm.currency_symbol,
    }));

  const formatContent = (content) =>
    content?.length > 0
      ? content?.map((elm, index) => {
          const dataTestIndex = content.length - 1 - index;
          return {
            id: elm?.id,
            value: (
              <InputText
                errorMessage={apiErrorResponse?.value && "api_error_salary"}
                maxLength={MAX_LENGTH_SALARY}
                value={elm.value}
                type={VAR_TYPES.NUMBER}
                onChange={(val) => {
                  const typedValue = Number(val.target.value);
                  onChange({
                    getUpdatedValues,
                    id: elm?.id,
                    key: "value",
                    setData,
                    data,
                    val: typedValue,
                  });
                  if (!Number.isNaN(typedValue)) {
                    setLastEditedSalary(typedValue);
                    setLastEditedSalaryRowId(elm?.id);
                  }
                }}
              />
            ),
            currency_id: (
              <InputSelect
                id="currency_id"
                value={elm.currency_id || currencyDefaultValue}
                options={formatCurrencyList()}
                name="currency_id"
                setValue={(name, val) =>
                  onChange({
                    getUpdatedValues,
                    id: elm?.id,
                    key: name,
                    setData,
                    data,
                    val,
                  })
                }
                dataTestId={`${MODAL_CONTENT.MANAGE_SALARY}_modal-currency-${dataTestIndex}`}
              />
            ),
            comment: (
              <InputText
                value={elm.comment}
                onChange={(val) =>
                  onChange({
                    getUpdatedValues,
                    id: elm?.id,
                    key: "comment",
                    setData,
                    data,
                    val: val.target.value,
                  })
                }
                dataTestId={`${MODAL_CONTENT.MANAGE_SALARY}_modal-comment-${dataTestIndex}`}
              />
            ),
            establishment_date: (
              <div
                data-testid={`${MODAL_CONTENT.MANAGE_SALARY}_modal-establishment_date-${dataTestIndex}`}
              >
                <InputDateModal
                  lang={lang}
                  elm={elm}
                  onChange={(date) => {
                    const formattedDate = date
                      ? format(date, "yyyy-MM-dd")
                      : "";
                    onChange({
                      getUpdatedValues,
                      id: elm?.id,
                      key: "establishment_date",
                      setData,
                      data,
                      val: formattedDate,
                    });
                  }}
                />
              </div>
            ),
            ...renderCommonBlock({
              data,
              getUpdatedValues,
              id: elm.id,
              onDelete,
              setData,
              updated_at: elm.updated_at,
              updated_by: elm.updated_by,
              lang,
              setElementsToDelete,
              deleteError: data?.filter((e) => !e.new_row)?.length - 1 === 0,
              errorToDisplay: () => setError(ERROR_AT_LEAST_ONE_SALARY),
              deleteBtnTestId: `${MODAL_CONTENT.MANAGE_SALARY}_modal-delete_btn-${dataTestIndex}`,
            }),
          };
        })
      : [];

  const setError = (code) => {
    setCode(code);
    setStatus(POPUP_STATUS.ERROR);
  };

  return (
    <>
      <button
        className="button-style"
        onClick={onAddWithDefaultCurrency}
        data-testid={`${MODAL_CONTENT.MANAGE_SALARY}_modal-add_salary_btn`}
      >
        <Typography
          className="link-1 violet"
          type="h2"
          label={
            <>
              +{" "}
              <FormattedMessage
                id="add_salary"
                defaultMessage="Ajouter un salaire"
              />
            </>
          }
        />
      </button>
      <br />
      <AlertPopup
        code={code}
        setCode={setCode}
        setStatus={setStatus}
        status={status}
      />
      <Table
        id="salary"
        header={header}
        content={formatContent(data)}
        tableOverflow={"auto"}
        tableWidth={phoneSize ? "max-content" : "100%"}
      />
      <br />
      <br />
      <ModalFooter
        onClose={onClose}
        onValidate={onClickValidate}
        disabled={entityStatus === QUERY_STATUS.LOADING}
        modalType={MODAL_CONTENT.MANAGE_SALARY}
      />
      <SalaryValidationModal
        open={showSalaryValidationModal}
        coefOptions={coefOptions}
        warning={salaryWarning}
        onClose={() => setShowSalaryValidationModal(false)}
        onSubmit={handleSalaryValidationSubmit}
      />
    </>
  );
};

ManageSalary.propTypes = {
  currencyListByBranch: PropTypes.array,
  currencyDefault: PropTypes.array,
  userSalary: PropTypes.array,
  getUpdatedValues: PropTypes.func,
  lang: PropTypes.string,
  setElementsToDelete: PropTypes.func,
  onClose: PropTypes.func,
  onValidate: PropTypes.func,
  entityStatus: PropTypes.string,
  userCoefPositionList: PropTypes.array,
  userType: PropTypes.string,
  syntecCoef: PropTypes.array,
  csltArrivalDate: PropTypes.string,
};

export default ManageSalary;
